import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Button, TableContainer } from '@mui/material';
import styled from 'styled-components';
import { Card, Col, Input, Row, Tabs } from 'antd';
import Loading from 'components/Loading';
import sortBy from 'lodash/sortBy';
import COLOR from 'utils/constants/color';
import { useDispatch } from 'react-redux';
import { handleArchiveCanvasCourse, handleGetOrgCanvasCourses } from 'store/classes/thunks';
import { useCurrentOrg } from 'store/organizations/selectors';
import UserCourseTable from './UserCourseTable';

const Wrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${COLOR.white};
  border-radius: 3px;
  padding: 16px;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
  width: 100%;
`;

export default function CoursesTable({
    users,
    schools,
    userloading,
    setUserLoading
}) {
    const dispatch = useDispatch();
    const org = useCurrentOrg();

    // raw data + UI state
    const [courses, setCourses] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 100 });
    const [activeTab, setActiveTab] = useState('1');
    const [selection, setSelection] = useState({ list: [], active: [], deactive: [] });
    const [modal, setModal] = useState({ open: false, action: '', count: 0 });
    const [loadingIds, setLoadingIds] = useState([]);
    const prevOrgIdRef = useRef(org?.id);

    // fetch once on mount (and when org.id changes)
    const fetchCourses = useCallback(async () => {
        setUserLoading(true);
        const data = await dispatch(handleGetOrgCanvasCourses(org.id));
        setUserLoading(false);
        setCourses(data);
    }, [dispatch, org.id, setUserLoading]);

    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    useEffect(() => {
        if (prevOrgIdRef.current !== undefined && prevOrgIdRef.current !== org?.id) {
            setSearchQuery('');
        }
        prevOrgIdRef.current = org?.id;
    }, [org?.id]);

    // apply tab + search filter, then sort
    const filteredData = useMemo(() => {
        let arr;
        switch (activeTab) {
            case '1':
                arr = courses.filter(c => !c.disabled && !c.admin_marked);
                break;
            case '2':
                arr = courses.filter(c => !c.disabled && c.admin_marked);
                break;
            case '3':
                arr = courses.filter(c => c.disabled && c.admin_marked);
                break;
            default:
                arr = [];
        }
        if (searchQuery.length > 2) {
            const q = searchQuery.toLowerCase();
            arr = arr.filter(c => c.displayName.toLowerCase().includes(q));
        }
        return sortBy(arr, ['displayName']);
    }, [courses, activeTab, searchQuery]);

    const currentPageData = useMemo(() => {
        const { page, rowsPerPage } = pagination;
        return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredData, pagination]);

    const selectedIds = useMemo(() => {
        if (activeTab === '1') return selection.list;
        if (activeTab === '2') return selection.active;
        return selection.deactive;
    }, [selection, activeTab]);

    // handlers
    const handleSearch = useCallback(e => {
        setSearchQuery(e.target.value);
        setPagination(p => ({ ...p, page: 0 }));
    }, []);

    const handleTabChange = useCallback(key => {
        setActiveTab(key);
        setPagination({ page: 0, rowsPerPage: 50 });
        setSelection({ list: [], active: [], deactive: [] });
        setSearchQuery('');
    }, []);

    const handleSelectAll = useCallback(e => {
        const ids = currentPageData.map(i => i.id);
        const key = activeTab === '1' ? 'list' : activeTab === '2' ? 'active' : 'deactive';
        setSelection(prev => ({
            ...prev,
            [key]: e.target.checked ? Array.from(new Set([...prev[key], ...ids])) : []
        }));
    }, [currentPageData, activeTab]);

    const handleSelectOne = useCallback((_, id) => {
        const key = activeTab === '1' ? 'list' : activeTab === '2' ? 'active' : 'deactive';
        setSelection(prev => {
            const setIds = new Set(prev[key]);
            setIds.has(id) ? setIds.delete(id) : setIds.add(id);
            return { ...prev, [key]: Array.from(setIds) };
        });
    }, [activeTab]);

    const handleChangePage = useCallback((_, page) => {
        setPagination(p => ({ ...p, page }));
    }, []);

    const handleChangeRowsPerPage = useCallback(e => {
        setPagination({ page: 0, rowsPerPage: parseInt(e.target.value, 10) });
    }, []);

    const openModal = useCallback(action => {
        setModal({ open: true, action, count: selectedIds.length });
    }, [selectedIds.length]);

    const closeModal = useCallback(() => {
        setModal({ open: false, action: '', count: 0 });
    }, []);

    const confirmAction = useCallback(async () => {
        setLoadingIds(selectedIds);
        const payload = selectedIds.map(id => {
            const { canvas_course_id, orgId } = courses.find(c => c.id === id);
            return { canvas_course_id, orgId };
        });
        await dispatch(
            handleArchiveCanvasCourse(
                payload,
                modal.action === 'Disabled'  // true = deactivate, false = activate
            )
        );
        setLoadingIds([]);
        closeModal();
        fetchCourses();
        setSelection({ list: [], active: [], deactive: [] });
    }, [dispatch, selectedIds, courses, modal.action, closeModal, fetchCourses]);

    // build AntD tabs
    const tabCounts = {
        1: courses.filter(c => !c.disabled && !c.admin_marked).length,
        2: courses.filter(c => !c.disabled && c.admin_marked).length,
        3: courses.filter(c => c.disabled && c.admin_marked).length,
    };

    const items = ['1', '2', '3'].map(key => ({
        key,
        label: `${key === '1' ? 'List' : key === '2' ? 'Active' : 'Deactive'} (${tabCounts[key]})`,
        children: (
            <>
                {selectedIds.length > 0 && (
                    <ActionBar>
                        <span style={{ fontWeight: 600, marginRight: 8 }}>
                            selected ({selectedIds.length})
                        </span>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => openModal('Disabled')}
                            sx={{ mr: 1, fontSize: 12 }}
                        >
                            Deactivate
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => openModal('Active')}
                            sx={{ fontSize: 12 }}
                        >
                            Activate
                        </Button>
                    </ActionBar>
                )}
                <UserCourseTable
                    data={filteredData}
                    selectedRows={selectedIds}
                    handleCheckboxClick={handleSelectOne}
                    handleSelectAllClick={handleSelectAll}
                    rowsPerPage={pagination.rowsPerPage}
                    page={pagination.page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    userloading={userloading}
                    userTeacher={ users.filter(u => u.role === 'teacher')}
                    schools={schools}
                />
            </>
        )
    }));

    return (
        <>
            <Modal open={modal.open} onClose={closeModal}>
                <ModalContent>
                    Are you sure you want to {modal.action === 'Disabled' ? 'deactivate' : 'activate'}{' '}
                    {modal.count} course{modal.count !== 1 ? 's' : ''}?
                    <div style={{ display: 'flex', marginTop: 16 }}>
                        <Button onClick={closeModal} sx={{ mr: 1 }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={loadingIds.length > 0}
                            onClick={confirmAction}
                        >
                            {loadingIds.length > 0 ? <Loading size={20} white /> : 'Yes'}
                        </Button>
                    </div>
                </ModalContent>
            </Modal>

            <Wrapper>
                <Row gutter={[16, 16]}>
                    <Col xs={24}>
                        <h3>
                            View <strong>Course</strong> Records.
                        </h3>
                    </Col>
                    <Col xs={24}>
                        <Card type="inner" title="Filters">
                            <Input
                                placeholder="Search courses by name"
                                bordered={false}
                                style={{ height: 32 }}
                                value={searchQuery}
                                onChange={handleSearch}
                            />
                        </Card>
                    </Col>
                </Row>

                <TableContainer sx={{ mt: 4, backgroundColor: '#fff', p: 2 }}>
                    <Tabs activeKey={activeTab} onChange={handleTabChange} items={items} />
                </TableContainer>
            </Wrapper>
        </>
    );
}